import { useState, useRef, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getCurrentUser, getUserAfterNameChange, logout } from '../features/auth/authSlice'
import axios from 'axios'
import '../styles/chat.css'
import { BACKEND_URL, WS_BASE } from '../app/config'
import TypingIndicator from '../components/TypingIndicator'
import EmojiPicker from 'emoji-picker-react';
import InlineEmojiPicker from '../components/InlineEmojiPicker'
import { Settings, User, Users, ArrowUp } from 'lucide-react'
import UserSettings from '../components/UserSettings'
import { parseUserInfo, checkForEmojiLarge, formatTime, handleSettingsSave } from '../app/helpers/Utils'

/*
 * ChatScreen — a Discord-style server chat (FRONTEND ONLY).
 *
 * Everything below runs on local state + mock data so you can see and style
 * it immediately. Each spot that needs Django is marked with:  // ⇇ BACKEND
 *
 * Suggested wiring when you build the backend (see notes at the bottom of the
 * file for the Django models):
 *   1. On mount / channel change → GET  /api/chat/channels/<id>/messages/
 *   2. On send                   → POST /api/chat/channels/<id>/messages/
 *   3. Real-time               → open a WebSocket (Django Channels) to
 *                                  ws://.../ws/chat/<channelId>/ and push new
 *                                  messages into `messages` as they arrive.
 */

// --- Mock data (delete once the backend is live) ---------------------------

const MOCK_CHANNELS = [
  { id: 'general', name: 'general', topic: 'Family-wide chit-chat' },
  /* { id: 'random', name: 'random', topic: 'Anything goes' },
  { id: 'dev', name: 'dev', topic: 'Engineering & deploys' },
  { id: 'support', name: 'support', topic: 'Help & questions' }, */
]
// Backend origin + WebSocket origin now come from the environment — see app/config.js.

// Exchange the stored refresh token for a NEW access token.
// The access token dies after ~5 min; the refresh token lasts ~24h, so this
// keeps the socket authenticatable without forcing a re-login.

async function refreshAccessToken() {
  const refresh = localStorage.getItem('refreshToken')
  if (!refresh) return null;
  try {
    const { data } = await axios.post(`${BACKEND_URL}/api/token/refresh/`, { refresh })
    localStorage.setItem('accessToken', data.access) // keep it fresh for the REST side too
    return data.access
  } catch (err) {
    // Refresh token itself is expired/invalid -> the user must log in again.
    console.warn("Chat: token refresh failed - please log in again.", err?.response?.status)
    return null
  }
}

const ChatScreen = () => {
  const { userInfo } = useSelector((state) => state.auth)
  const dispatch = useDispatch()
  const navigate = useNavigate();
  // const currentUser = userInfo?.username || 'You'
  const [currentUser, setCurrentUser] = useState(userInfo?.username || 'You')
  const [oldName, setOldName] = useState(currentUser);

  const [activeChannel, setActiveChannel] = useState(MOCK_CHANNELS[0])
  const [messages, setMessages] = useState([])// filled by the server's history frame
  const [onlineUsers, setOnlineUsers] = useState([])
  const [UsersInfo, setUsersInfo] = useState([])
  const [allUsers, setAllUsers] = useState([]) // every registered username (the directory)
  const [draft, setDraft] = useState('')
  const [emoji, setEmoji] = useState('')
  const [showMembers, setShowMembers] = useState(false) // mobile-only members drawer
  const [showSettings, setShowSettings] = useState(false) // mobile-only members drawer
  const [typingUsers, setTypingUsers] = useState([]) // array of our actively typing users
  const [selectedTheme, setSelectedTheme] = useState() 


  const scrollRef = useRef(null)
  const socketRef = useRef(null) // holds the live WebSocket
  const isTypingRef = useRef(false);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    if(currentUser && onlineUsers?.length > 0 && UsersInfo?.length > 0){
      if( userInfo?.username && userInfo.username !== oldName ){
        setOnlineUsers(prev => prev.map(u => u === oldName ? userInfo?.username : u))
        setUsersInfo(prev => prev.map(u => u.username === oldName ? {...u, username: userInfo?.username} : u))
        setAllUsers(prev => prev.map(u => u === oldName ? userInfo?.username : u));
        setMessages((prev) => prev.map((u) => u.username === oldName ? {...u, username: userInfo?.username} : u))
        setOldName(userInfo?.username)
      }
    }
  },[userInfo?.username, onlineUsers, UsersInfo, allUsers, currentUser, messages ])

  useEffect(() => {
    if (userInfo?.username) {
      setCurrentUser(userInfo.username);
      // console.log("userInfo effect", userInfo.username)
    }
  }, [userInfo?.username]); 

  // Get our selected theme from our user settings, defaults to "Discord" if none set
  /* useEffect(() => {
    if(selectedTheme)
      console.log(selectedTheme)
  },[selectedTheme]) */

  // Dedicated function to stop typing immediately and cleanly
  const stopTyping = () => {
    if (isTypingRef.current) {
      isTypingRef.current = false;

      // Send exactly one single stop event to the server
      if(socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ type: "type_stop" }))
        // console.log("Stopped Typing")
      }
    }
  }

  const CheckTheme = ({ message }) => {
    if(selectedTheme === "Discord") {
      return (
        <p className={`chat__text ${checkForEmojiLarge(message.body) ? 'large_emoji' : ''} `}>{message.body}</p>

      );
    } else if(selectedTheme === "iMessage") {
      return (
            <div className={`imessage-row chat__text ${message.username !== currentUser ? "imessage-row--left" : "imessage-row--right"} `}>
              <div className={`imessage-bubble chat__text ${message.username !== currentUser ? "imessage-bubble--left" : "imessage-bubble--right"} ${checkForEmojiLarge(message.body) ? 'large_emoji' : ''}`}>
                  {message.body}
              </div>
            </div>
      )
    }
  }

  const handleInputChanged = (event) => {
    setDraft(event.target.value)
    // If the user cleared the text box entirely, stop typing immediately
    if(event.target.value.trim() === '') {
      stopTyping();
      return;
    }

    // Send start event only once when they begin typing
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      // console.log("Is Typing")
      const socket = socketRef.current
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: "type_start" }))
      }
    }
  }

  // Lock page scroll only while the chat is mounted
  useEffect(() => {
    document.body.classList.add('chat-open');
    return () => document.body.classList.remove('chat-open');
  }, []);

  // Load the full user directory a single time. "offline" is derived from this minus the live online set, so this never needs to re-run on presence changes.
  useEffect(() => {
    let active = true
    const loadUsers = async () => {
      // Reuse our refresh helper so this call always has a valid access token(the directory endpoint requires auth).
      const token = await refreshAccessToken()
      if (!token || !active) return
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/users/`, {
          headers: { Authorization : `Bearer ${token}`},
        })
        setAllUsers(data.map((u) => u.username))
        setUsersInfo(data.map((u) => u))
      } catch (err) {
        console.warn("Could not load user directory:", err?.response?.status)
      }
    }
    loadUsers()
    return () => { active = false }
  }, [])

  // Open a fresh WebSocket whenever the active channel changes, and automatically reconnect (with a fresh token) if it ever drops.
  useEffect(() => {
    let socket = null
    let reconnectTimer = null
    let cancelled = false // set on cleanup so we stop reconnecting when unmounting/switching
    const connect = async () => {
      // 1) Alway s get a FRESH access token before connecting.
      const token = await refreshAccessToken()

      // Effect was torn down mid-await (room switch / left the page) → bail quietly.
      if (cancelled) return; 

      // Refresh token is dead -> real logout, then send them to login.
      if (!token) {
        dispatch(logout())  // clears tokens + redux auth (stops the re-auth loop)
        navigate('/login'); 
        return
      }//return // refresh failed (re-login) or effect torn down mid-await

      // 2) open the socket with the fresh token.
      socket = new WebSocket(`${WS_BASE}/ws/chat/${activeChannel.id}/?token=${token}`)
      socketRef.current = socket
      console.log("connected to new socket")

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data)
        if (data.type === "history") { 
          setMessages(data.messages)                     // full backlog on (re)connect
        } else if (data.type === 'message') {
          setMessages((prev) => [...prev, data.message]) // one live message
        } else if (data.type === 'presence_state') {
          // Full roster, sent once right afer we connect.
          setOnlineUsers(data.users)
          // setUsersInfo(data.userInfo)
        } else if (data.type === 'presence') {
          // A single join/leave. Dedupe on join because we also receive our own.
          setOnlineUsers((prev) => {
            if (data.action === 'join') {
              return prev.includes(data.username) ? prev : [...prev, data.username]
            }
            return prev.filter((u) => u !== data.username)
          })
        }  else if (data.type === 'chat_event') {
          if (data.action === 'type_start') {
            if (data.username !== currentUser)
              setTypingUsers((prev) => prev.includes(data.username) ? prev : [...prev, data.username]);
          } else if(data.action === 'type_stop') {
            setTypingUsers((prev) => prev.filter((user) => user !== data.username));
          }
          
          // console.log(data)
          // setTypingUsers((prev) => prev.filter((user) => user !== data.user));
          // Users begin to type or stop typing, render an element or component like a chat bubble
          
        }
      }

      socket.onclose = () => {
        console.log("connection lost, recconnecting..")
        if (cancelled) return // we closed it on purpose(cleanup) -> don't reconnect
        // Unexpected drop (server reload, token, network) -> reconnect shortly,
        // which calls connect() again and refreshes the token first.
        reconnectTimer = setTimeout(connect, 1500)
      }
    }

    setMessages([]) // clear while (re)connecting; the history frame will refill it
    setOnlineUsers([])
    connect()

    // Cleanup: runs on channel switch OR when leaving the chat page.
    return () => {
      cancelled = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (socket) socket.close()
    }
  }, [activeChannel])

  // Auto-scroll to the newest message.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages, activeChannel])

  // Group consecutive messages from the same author (Discord-style stacking).
  const grouped = useMemo(() => {
    const out = []
    messages.forEach((m, i) => {
      const last = out[out.length - 1]
      if (last && last?.username === m?.username) {
        last.items.push(m)
      } else {
        out.push({ username: m.username, items: [m] })
      }
    })
    return out
  }, [messages])

  
  
  // Offline = everyone in the directory who ins't currently online. Recomputes instantly in the browser whenever either list changes - zero network.
  const offlineUsers = useMemo(() => { // same as const offlineUsers = useMemo(() => allUsers.filter((u) => !onlineUsers.includes(u)), [allUsers, onlineUsers]) single line
    const olu = allUsers.filter((u) => !onlineUsers.includes(u))
    return olu;
  }, [allUsers, onlineUsers])

  const handleSend = (e) => {
    e.preventDefault()
    const body = draft.trim()
    if (!body) return

    const socket = socketRef.current
    if (socket && socket.readyState === WebSocket.OPEN) {
      // Just send it up. The server saves it and broadcasts it back to everyone - including us = so it lands via socket.onmessage above.
      socket.send(JSON.stringify({ body })) 
      stopTyping()
      setDraft('');
    }
  }

  

  useEffect(() => {
    if (emoji) {
      // Append Emoji to the draft, this always comes after the draft is set in input change so we always have the latest input
      setDraft((prev) => prev + emoji)
      // Set emoji to null after setting to draft, prevents this useEffect from running again since emoji is now false.
      setEmoji();
    } 
  },[emoji])

  


  

  return (
    <div className='chat'>
      {/* ---- Channel sidebar ---- */}
      <aside className='chat__channels'>
        <div className='chat__server'>
          <span className='chat__server-name'>Fam Chat Server</span>
        </div>
        <div className='chat__channels-list'>
          <p className='chat__section-label'>Text Channels</p>
          {MOCK_CHANNELS.map((c) => (
            <button
              key={c.id}
              className={`chat__channel ${activeChannel.id === c.id ? 'is-active' : ''}`}
              onClick={() => setActiveChannel(c)}
            >
              <span className='chat__hash'>#</span>
              {c.name}
            </button>
          ))}
        </div>

        {/* Current-user footer, like Discord's bottom-left card */}
        <div className='chat__me'>
          <span className='chat__avatar' style={{ background: parseUserInfo(currentUser, UsersInfo) }}>
            {currentUser.charAt(0).toUpperCase()}
          </span>
          <span className='chat__me-name'>{currentUser}</span>
          <span className='chat__status-dot' title='Online' />
        </div>
        
      </aside>

      {/* ---- Main chat column ---- */}
      <section className='chat__main '>
        <header className='chat__header'>
          <span className='chat__hash chat__hash--lg'>#</span>
          <span className='chat__header-name'>{activeChannel.name}</span>
          <span className='chat__divider' />
          <span className='chat__topic'>{activeChannel.topic}</span>
          {/* Mobile-only: opens the members drawer. Hidden on desktop via CSS. */}
          <button
            type='button'
            // className='chat__members-toggle'
            onClick={() => setShowSettings((v) => !v)}
            aria-label='Toggle members list'
          >
            <Settings /> 
          </button>
          <button
            type='button'
            className='chat__members-toggle'
            onClick={() => setShowMembers((v) => !v)}
            aria-label='Toggle members list'
          >
            <Users />({onlineUsers.length})
          </button>
          
        </header>

        <div className={`chat__messages  ${selectedTheme}`} ref={scrollRef}>
          <div className='chat__welcome'>
            <div className='chat__welcome-icon'>#</div>
            <h2>Welcome to #{activeChannel.name}</h2>
            <p>This is the start of the #{activeChannel.name} channel.</p>
          </div>

          {grouped.map((group, gi) => (
            <div className={`chat__group ${selectedTheme}`} key={gi}>
              {selectedTheme === "Discord" ? <span className='chat__avatar chat__avatar--msg' style={{ background: parseUserInfo(group.username, UsersInfo) }}>{group.username.charAt(0).toUpperCase()}</span>: 
              ""}
              <div className='chat__group-body'>
                <div className='chat__group-head '>
                {selectedTheme === "Discord" ? 
                  <span className={`chat__author ${group.username === currentUser ? 'is-me' : ''}`}>{group.username}</span>: 
                  // allUsers.length < 2 ? 
                  <span className={`chat__author ${group.username === currentUser ? 'hidden' : ''}`}>{group.username}</span>/* : '' */}
                    
                  {selectedTheme === "Discord" ? 
                  <span className={`chat__time ${group.username === currentUser ? '' : 'flex'} `}>{formatTime(group.items[0].timestamp)}</span> : 
                  <span className={`chat__time ${group.username === currentUser ? 'imessage-row imessage-row--right' : 'imessage-row imessage-row--left'} `}>{formatTime(group.items[0].timestamp)}</span>}
                </div>
                {group.items.map((m) => (
                  <CheckTheme key={m.id} message={m} />
                  // <p className={`chat__text ${checkForEmojiLarge(m.body) ? 'large_emoji' : ''} ${checkTheme} `} key={m.id}>{m.body}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
        
        {/*  Typing Indicator Bubble */}
        <div className='chat_indicator_container  '>
          {typingUsers.length > 0 && <TypingIndicator usernames={typingUsers}/>}
        </div>
        
        
        {/* ---- Composer ---- */}
        <form className={`chat__composer ${selectedTheme}`}  onSubmit={handleSend}>
          <div className={`input-container ${selectedTheme === "Discord" ? "" : "!bg-[var(--bg-iMessage)] border border-[var(--bg-input)] !rounded-3xl"}`}>
            <input
              className='chat__input' 
              type='text'
              value={draft}
              onChange={handleInputChanged}
              placeholder={`${selectedTheme === "Discord" ? `Message #${activeChannel.name}` : 'Message'} `}
              aria-label={`Message #${activeChannel.name}`}
            />
            <div  className='flex'>
              <InlineEmojiPicker  onEmojiClick={(emoji) => setEmoji(emoji.emoji)} />
            </div> 
          </div>
          
          <button className='chat__send ' type='submit' disabled={!draft.trim()}>
            {selectedTheme === "Discord" ? "Send" : <ArrowUp />}
          </button>
        </form>
      </section>

      {/* Mobile backdrop: tap outside the drawer to close it */}
      {showMembers && (
        <div className='chat__backdrop' onClick={() => setShowMembers(false)} />
      )}

      {showSettings && (
        <div className='chat__backdrop' onClick={() => setShowSettings(false)} />
      )}

      
      {/* ---- User Settings ---- */}
      <UserSettings 
      open = {showSettings} 
      user={currentUser}
      userDetails = {UsersInfo}
      selectedTheme={(e) => setSelectedTheme(e)}
      onColorChanged={handleSettingsSave}
      onUsernameChange={setCurrentUser}
      />

      {/* ---- Member list ---- */}
      <aside className={`chat__members ${showMembers ? 'is-open' : ''}`}>
        <p className='chat__section-label'>
          Online — {onlineUsers.length}
        </p>
        {onlineUsers.map((username,i) => (
          <div className='chat__member' key={i+"_"+username}>
            <span className='chat__avatar chat__avatar--sm' style={{ background: parseUserInfo(username, UsersInfo)}}>
              {username.charAt(0).toUpperCase()}
            </span>
            <span className='chat__member-name'>{username}</span>
            <span className='chat__status-dot' />
          </div>
        ))}

        <p className='chat__section-label'>
          Offline — {offlineUsers?.length}
        </p>
        {offlineUsers?.map((username, i) => (
          <div className='chat__member is-offline' key={i+"_"+username}>
            <span className='chat__avatar chat__avatar--sm' style={{ background: parseUserInfo(username, UsersInfo) }}>
              {username.charAt(0).toUpperCase()}
            </span>
            <span className='chat__member-name'>{username}</span>
          </div>
        ))}
      </aside>
    </div>
  )
}

export default ChatScreen
